import type { PublicDisplaySnapshot } from "./shared/types";

export type AdminErrorKind = "credentials" | "session" | "request";

/** Error raised by an admin API call while retaining its HTTP status for UI handling. */
export class AdminRequestError extends Error {
  readonly status: number;
  readonly kind: AdminErrorKind;

  constructor(message: string, status: number, kind: AdminErrorKind = "request") {
    super(message);
    this.name = "AdminRequestError";
    this.status = status;
    this.kind = kind;
  }
}

export function isAdminCredentialError(error: unknown): error is AdminRequestError {
  return error instanceof AdminRequestError && error.kind === "credentials";
}

export function isAdminSessionError(error: unknown): error is AdminRequestError {
  return error instanceof AdminRequestError && error.kind === "session";
}

export async function fetchSnapshot(): Promise<PublicDisplaySnapshot> {
  const response = await fetch("/api/display/snapshot", {
    cache: "no-store",
    headers: {
      accept: "application/json",
      "cache-control": "no-cache"
    }
  });

  if (!response.ok) {
    throw new Error(`Snapshot request failed: ${response.status}`);
  }

  return (await response.json()) as PublicDisplaySnapshot;
}

export async function loginAdmin(password: string): Promise<{ ok: boolean; sessionToken: string }> {
  const response = await fetch("/api/admin/login", {
    method: "POST",
    headers: {
      "content-type": "application/json"
    },
    body: JSON.stringify({ password })
  });

  if (!response.ok) {
    throw new AdminRequestError(`Admin login failed: ${response.status}`, response.status, response.status === 401 ? "credentials" : "request");
  }

  return (await response.json()) as { ok: boolean; sessionToken: string };
}

export async function changeAdminPassword(token: string, currentPassword: string, newPassword: string): Promise<{ ok: boolean }> {
  return postAdminJson("/api/admin/password", token, { currentPassword, newPassword });
}

export async function postAdminJson<TResponse>(path: string, token: string, body: unknown): Promise<TResponse> {
  const response = await fetch(path, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${token}`
    },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    throw new AdminRequestError(`Admin request failed: ${response.status}`, response.status, response.status === 401 ? "session" : "request");
  }

  return (await response.json()) as TResponse;
}

export async function fetchAdminJson<TResponse>(path: string, token: string): Promise<TResponse> {
  const response = await fetch(path, {
    headers: {
      accept: "application/json",
      authorization: `Bearer ${token}`
    }
  });

  if (!response.ok) {
    throw new AdminRequestError(`Admin request failed: ${response.status}`, response.status, response.status === 401 ? "session" : "request");
  }

  return (await response.json()) as TResponse;
}
